import { EntityListPage, type FilterField, type DataTableColumn, type CardConfig } from '@/components/common'
import { resolvePresetRange } from '@/lib/datePresets'
import { useTranslation } from '@/contexts/TranslationContext'
import { useUnit } from '@/contexts/UnitContext'
import { matchesSearch } from '@/lib/search'
import { 
  getAllCategories, 
  createCategory, 
  updateCategory, 
  deleteCategory,
  type AssetCategory
} from '@/lib/services/categories'

// Input type para o formulário
interface CategoryInput {
  name: string
}

export function CategoriesPage() {
  const { t } = useTranslation()
  const { userRole } = useUnit()
  const tc = (key: string) => t(`settings.assets.categories.${key}`)

  // Filter fields configuration
  const filterFields: FilterField[] = [
    {
      key: 'name',
      label: 'Nome da Categoria',
      type: 'text',
      placeholder: 'Filtrar por nome...'
    },
    {
      key: 'created_at',
      label: 'Data de Criação',
      type: 'daterange'
    }
  ]

  // Table columns configuration
  const tableColumns: DataTableColumn<AssetCategory>[] = [
    {
      key: 'name',
      label: tc('list.columns.name'),
      render: (category) => (
        <div className="font-medium text-slate-900 dark:text-white">{category.name}</div>
      )
    },
    {
      key: 'created_at',
      label: tc('list.columns.createdAt'),
      render: (category) => (
        <span className="text-sm text-slate-600 dark:text-slate-400">
          {new Date(category.created_at).toLocaleDateString('pt-BR')}
        </span>
      )
    }
  ]

  // Entity configuration
  const config = {
    entityType: 'categories',
    filterFields,
    
    loadData: async () => {
      return await getAllCategories()
    },
    
    createEntity: async (data: CategoryInput) => {
      return await createCategory(data.name)
    },
    
    updateEntity: async (id: string, data: CategoryInput) => {
      return await updateCategory(id, data.name)
    },
    
    deleteEntity: async (id: string) => {
      await deleteCategory(id)
    },
    
    applyFilters: (categories: AssetCategory[], searchQuery: string, filterValues: any) => {
      return categories.filter(category => {
        // Quick search
        if (searchQuery.trim()) {
          if (!matchesSearch(category.name, searchQuery)) {
            return false
          }
        }

        // Advanced filters
        if (filterValues.name && filterValues.name.trim()) {
          if (!matchesSearch(category.name, filterValues.name)) {
            return false
          }
        }

        if (filterValues.created_at && (filterValues.created_at.start || filterValues.created_at.end || filterValues.created_at.preset)) {
          let start = filterValues.created_at.start || null
          let end = filterValues.created_at.end || null

          if (filterValues.created_at.preset) {
            const resolved = resolvePresetRange(filterValues.created_at.preset)
            start = resolved.start
            end = resolved.end
          }

          const categoryDate = new Date(category.created_at).toISOString().split('T')[0]
          if (start && categoryDate < start) return false
          if (end && categoryDate > end) return false
        }

        return true
      })
    },
    
    validateForm: (data: CategoryInput) => {
      const errors: Record<string, string> = {}
      
      if (!data.name.trim()) {
        errors.name = tc('messages.nameRequired')
      } else if (data.name.trim().length < 2) {
        errors.name = tc('messages.nameMinLength')
      } else if (data.name.trim().length > 100) {
        errors.name = tc('messages.nameMaxLength')
      }
      
      return errors
    }
  }

  // Labels
  const labels = {
    title: tc('list.title'),
    description: tc('list.description'),
    newButton: tc('actions.new'),
    searchPlaceholder: tc('filters.searchPlaceholder'),
    emptyTitle: tc('list.empty.title'),
    emptyDescription: tc('list.empty.description'),
    emptyActionLabel: tc('actions.new'),
    modalTitleNew: tc('record.new'),
    modalTitleEdit: tc('record.edit'),
    deleteTitle: tc('record.delete'),
    deleteMessage: tc('record.deleteConfirm'),
    deleteButton: tc('actions.delete'),
    cancelButton: tc('actions.cancel'),
    submitButton: tc('actions.create'),
    updateButton: tc('actions.update'),
    viewCard: tc('filters.viewCard'),
    viewTable: tc('filters.viewTable'),
    clearSearch: tc('filters.clear')
  }

  // Icon
  const icon = (
    <svg className="h-7 w-7 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
    </svg>
  )

  // Card configuration (replaces manual renderCard - enables automatic customization!)
  const cardConfig: CardConfig<AssetCategory> = {
    getIcon: () => (
      <svg className="h-5 w-5 text-slate-700 dark:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
      </svg>
    ),
    getTitle: (category) => category.name,
    getSubtitle: (category) => `Criada em ${new Date(category.created_at).toLocaleDateString('pt-BR')}`,
    fields: [
      {
        key: 'id',
        label: 'ID',
        getValue: (category) => (
          <code className="text-xs font-mono text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
            {category.id.substring(0, 8)}...
          </code>
        )
      },
      {
        key: 'created',
        label: 'Data de Criação',
        getValue: (category) => new Date(category.created_at).toLocaleDateString('pt-BR')
      }
    ],
    editLabel: tc('actions.edit'),
    deleteLabel: tc('actions.delete')
  }

  // Form render
  const renderForm = (
    formData: CategoryInput,
    setFormData: (data: CategoryInput) => void,
    formErrors: Record<string, string>,
    submitting: boolean,
    editingEntity: AssetCategory | null,
    mode: 'create' | 'edit' | 'view'
  ) => (
    <div className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {tc('record.fields.name')}
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder={tc('record.fields.namePlaceholder')}
          className={`w-full px-4 py-2 rounded-lg border ${
            formErrors.name ? 'border-red-300' : 'border-slate-300'
          } dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500`}
          disabled={submitting || mode === 'view'}
          autoFocus
        />
        {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
      </div>
    </div>
  )

  return (
    <EntityListPage<AssetCategory, CategoryInput>
      config={config}
      labels={labels}
      icon={icon}
      filterFields={filterFields}
      tableColumns={tableColumns}
      userRole={userRole}
      namespace="settings.assets.categories"
      actionsColumnLabel={tc('record.fields.actions')}
      cardConfig={cardConfig}
      renderForm={renderForm}
      entityToFormData={(category) => ({
        name: category.name
      })}
      emptyFormData={() => ({
        name: ''
      })}
    />
  )
}
